import React, { FC, useEffect, useState } from "react";
import Icon from "../../../../components/Icon/Icon";
import Form from "../../../../components/Form/Form";
import { ValidatorsE } from "../../../../components/Form/yupValidations";
import { FormFieldT, VersionT } from "../../../../models/models";
import { toast } from "react-toastify";
import Button, { ButtonModeE } from "../../../../components/Button/Button";
import {
  isMobile,
  isMobileOrTablet,
} from "../../../../utils/helpers/mediaQueries";
import {
  CampaignWizardMode,
  FileTypeE,
  IconColorE,
  LangE,
  TooltipPlaceE,
  UserRoleE,
} from "../../../../models/enums";
import AddVersionSlider from "../../../../sliders/AddVersionSlider/AddVersionSlider";
import { Span } from "../../../../components/Text/Text";
import {
  fileTypeHebrewSO,
  fileTypeSO,
  selectValue,
} from "../../../../utils/helpers/selectOptions";
import { useCallback } from "react";
import { convertNumToMinSec } from "../../../../utils/helpers/convertNumToMinSec";
import { TOAST_DURATION } from "../../../../utils/helpers/constants";
import Tooltip from "../../../../components/Tooltip/Tooltip";

import styles from "./versionRow.module.scss";
import { useAppSelector } from "../../../../store/hooks";
import { selectCurrentLang } from "../../../../store/selectors/appSelectors";
import InfoTooltip from "../../../../components/InfoTooltip/InfoTooltip";

type VersionRowPropTypes = {
  version: VersionT;
  onSubmit: (
    data: any,
    id: number,
    advId: string,
    campaignId: string,
    nextProjectNumber: number,
    mode: CampaignWizardMode,
  ) => void;
  onDelete: (id: number) => void;
  withAdId?: boolean;
  index: number;
  versionsAmount: number;
  advId?: string;
  campaignId?: string;
  nextProjectNumber?: number;
  mode?: CampaignWizardMode;
  currentStep: number;
};

const setContinueButtonDisabled = (isDisabled: boolean) => {
  // const continueButton = document.getElementById('wizard-continue-button-1');
  // if (continueButton) {
  //     (continueButton as any).disabled = isDisabled;
  // }
};

const VersionRow: FC<VersionRowPropTypes> = (props) => {
  const {
    version: {
      Name,
      FileType,
      RequiredLength,
      isChannel5or9,
      useFor2Spots,
      AdId,
      Id,
    },
    onSubmit,
    onDelete,
    withAdId = true,
    index,
    versionsAmount,
    advId = "NONE",
    campaignId = "NONE",
    nextProjectNumber = 0,
    mode,
    currentStep,
  } = props;

  const lang = useAppSelector(selectCurrentLang);
  const [isEditMode, setIsEditMode] = useState(
    !isMobileOrTablet() && (!RequiredLength || !Name),
  );
  const [showBottomSlider, setShowBottomSlider] = useState(
    isMobileOrTablet() && (!RequiredLength || !Name) && currentStep === 1,
  );
  const [length, setLength] = useState(RequiredLength);
  const [name, setName] = useState(Name);
  const [type, setType] = useState(FileType);
  const [for2Spots, setFor2Spots] = useState(useFor2Spots);
  const [is5or9, setIs5or9] = useState(isChannel5or9);
  const [showNameLengthHint, setShowNameLengthHint] = useState(false);

  const handleKeyPress = useCallback((e: any) => {
    if (e.keyCode === 13) {
      (document.activeElement as any).blur();
    }
  }, []);

  const handleAddVersion = () => {
    const saveButton = document.getElementById(`save-version-row-${index}`);
    if (saveButton) {
      saveButton.click();
    }
    const addEmptyVersionEvent = new CustomEvent("addemptyversion");
    window.dispatchEvent(addEmptyVersionEvent);
  };

  useEffect(() => {
    setContinueButtonDisabled(!RequiredLength || !Name);

    window.addEventListener("keypress", handleKeyPress);
    window.addEventListener("addversion", handleAddVersion);

    return () => {
      window.removeEventListener("keypress", handleKeyPress);
      window.removeEventListener("addversion", handleAddVersion);
    };
  }, []);

  useEffect(() => {
    if (isMobileOrTablet() && (!RequiredLength || !Name) && currentStep === 1) {
      setShowBottomSlider(true);
    }
  }, [currentStep]);

  const handleFocusName = () => {
    setShowNameLengthHint(true);
  };

  const handleBlurName = () => {
    setShowNameLengthHint(false);
  };

  useEffect(() => {
    // block continue button if is edit mode
    setContinueButtonDisabled(isEditMode);

    const nameField = document.getElementsByName("Name")?.[0];

    if (nameField) {
      nameField.addEventListener("focus", handleFocusName);
      nameField.addEventListener("blur", handleBlurName);
    }
    return () => {
      if (nameField) {
        nameField.removeEventListener("focus", handleFocusName);
        nameField.removeEventListener("blur", handleBlurName);
      }
    };
  }, [isEditMode]);

  const NameExtraContent = () => (
    <div>
      {showNameLengthHint && name.length !== 0 && name.length <= 45 ? (
        <p className={styles.maxNameLengthHint}>
          <Span>Max allowed chars</Span> {name?.length || 0}/45
        </p>
      ) : null}
    </div>
  );

  const fields: FormFieldT[] = [
    {
      label: isMobileOrTablet() ? "Name" : "",
      name: "Name",
      validation: ValidatorsE.REQUIRED_MAX_45,
      defaultValue: Name,
      onChange: (v: string) => setName(v),
      extraContent: () => <NameExtraContent />,
    },
    {
      label: isMobileOrTablet() ? "Type" : "",
      name: "Type",
      validation: ValidatorsE.SELECT_REQUIRED,
      selectOptions: lang === LangE.EN ? fileTypeSO : fileTypeHebrewSO,
      defaultValue: selectValue(
        lang === LangE.EN ? fileTypeSO : fileTypeHebrewSO,
        +FileType,
      ),
      onChange: ({ value }: any) => setType(value),
      props: {
        isSearchable: false,
      },
    },
    {
      label: isMobileOrTablet() ? "Length" : "",
      name: "Length",
      type: "number",
      validation: ValidatorsE.REQUIRED_NUMBER_MIN_1,
      defaultValue: RequiredLength,
      onChange: (v: any) => setLength(isNaN(parseInt(v)) ? 1 : v),
    },
  ];

  const toggleShowBottomSlider = () => {
    setShowBottomSlider((prev) => !prev);
  };

  const toggleEditMode = () => {
    if (isMobile()) {
      toggleShowBottomSlider();
    } else {
      setIsEditMode((prev) => !prev);
    }
  };

  const handleSaveChanges = (data: any) => {
    const body = {
      FileType: data.Type.value,
      Name: data.Name,
      RequiredLength: data.Length,
      isChannel5or9: is5or9,
      useFor2Spots: +length === 6 && for2Spots,
    };

    // if version name length is not correct
    if (name.length < 1 || name.length > 45) {
      // hide hint because length error will be shown
      setShowNameLengthHint(false);
      return;
    }
    onSubmit(body, index, advId, campaignId, nextProjectNumber, mode);
    setIsEditMode(false);

    setContinueButtonDisabled(false);
  };

  const deleteRow = () => {
    if (versionsAmount > 1) {
      onDelete(index);
    } else {
      toast("You should have at least one version", {
        type: "error",
        autoClose: TOAST_DURATION,
      });
    }
    setContinueButtonDisabled(false);
  };

  const Elements = useCallback(
    () => (
      <div>
        <label htmlFor="ch59">
          <div className={styles.checkboxWrapper}>
            <input
              type="checkbox"
              id="ch59"
              name="ch59"
              defaultChecked={is5or9}
              className={styles.checkbox}
              onChange={(e: any) => setIs5or9(e.target.checked)}
            />
            <span className={styles.mark} />
            <Span className={styles.checkboxLabel}>
              Channel 5 or Channel 9 (MXF format)
            </Span>
            <InfoTooltip text="Mark this version if it is for Channel 5 or Channel 9 (MXF format)" />
          </div>
        </label>
        {+length === 6 && (
          <label htmlFor="use-version">
            <div className={styles.checkboxWrapper}>
              <input
                type="checkbox"
                id="use-version"
                name="use-version"
                defaultChecked={for2Spots}
                className={styles.checkbox}
                onChange={(e: any) => setFor2Spots(e.target.checked)}
              />
              <span className={styles.mark} />
              <Span className={styles.checkboxLabel}>
                Use this version as an ad and sponsorships as well
              </Span>
              <InfoTooltip text="Is this version will be used as an advertisement and sponsorships as well?" />
            </div>
          </label>
        )}
      </div>
    ),
    [length, isChannel5or9, useFor2Spots, is5or9, for2Spots],
  );

  return !isEditMode ? (
    !isMobile() ? (
      <div className={styles.wrapper}>
        <p>{Name}</p>
        <p>{FileTypeE[FileType]}</p>
        <p>{convertNumToMinSec(RequiredLength)}</p>
        <div className={styles.versionInfo}>
          <p>
            {is5or9 ? "For Channel 5 or Channel 9 (MXF format)" : ""}
            {is5or9 && (
              <Tooltip
                text="Mark this version if it is for Channel 5 or Channel 9 (MXF format)"
                delayShow={300}
                place={TooltipPlaceE.LEFT}
              >
                <div className={styles.infoTooltipWrapper}>
                  <Icon name="info" />
                </div>
              </Tooltip>
            )}
          </p>
          <p>
            {for2Spots && "Use as an advertisement and sponsorships as well"}
            {for2Spots && (
              <Tooltip
                text="Is this version will be used as an advertisement and sponsorships as well?"
                delayShow={300}
                place={TooltipPlaceE.LEFT}
              >
                <div className={styles.infoTooltipWrapper}>
                  <Icon name="info" />
                </div>
              </Tooltip>
            )}
          </p>
        </div>
        <div className={styles.buttons}>
          <Icon name="edit" onClick={toggleEditMode} title="Edit version" />
          {versionsAmount > 1 && (
            <Icon name="bin" onClick={deleteRow} title="Remove version" />
          )}
        </div>
      </div>
    ) : (
      <div className={styles.rowMobileWrapper}>
        {(Name || !showBottomSlider) && (
          <>
            <div>
              <div className={styles.mobileIconName}>
                <Icon
                  name="clockFilled"
                  color={IconColorE.GRAY}
                  title="Not uploaded yet"
                />
                <span>{Name}</span>
              </div>
              <div className={styles.actionIconsWrapper}>
                <Icon
                  name="edit"
                  onClick={toggleEditMode}
                  title="Edit version"
                />
                {versionsAmount > 1 && (
                  <Icon name="bin" onClick={deleteRow} title="Remove version" />
                )}
              </div>
            </div>
            <div>
              <div className={styles.versionInfo}>
                <p>{is5or9 ? "For Channel 5 or Channel 9 (MXF format)" : ""}</p>
                <p>
                  {for2Spots
                    ? "Use as an advertisement and sponsorships as well"
                    : ""}
                </p>
              </div>
              <span>{convertNumToMinSec(RequiredLength)}</span>
            </div>
            <div>
              <span>Advertisement</span>
              <span>MP4</span>
            </div>
          </>
        )}
        <AddVersionSlider
          item={props.version}
          visible={showBottomSlider}
          onClose={toggleShowBottomSlider}
          elements={Elements}
          fields={fields}
          onSubmit={handleSaveChanges}
          onDelete={() => onDelete(index)}
          versionsAmount={versionsAmount}
          index={index}
        />
      </div>
    )
  ) : (
    !isMobileOrTablet() && (
      <div className={styles.wrapperEditMode}>
        <Form
          onSubmit={handleSaveChanges}
          fields={fields}
          className={styles.formWrapper}
          formID={`version-row-${index}`}
          elements={<Elements />}
        />
        <div className={styles.buttons}>
          <Button
            mode={ButtonModeE.LINK}
            form={`version-row-${index}`}
            className={styles.approveButton}
            id={`save-version-row-${index}`}
          >
            Save
          </Button>
          {versionsAmount > 1 && (
            <Icon
              name="close"
              onClick={() => (!Name ? deleteRow() : toggleEditMode())}
              color={IconColorE.GRAY}
              title="Cancel editing"
            />
          )}
        </div>
      </div>
    )
  );
};

export default VersionRow;
